import React, { useCallback, useEffect, useRef, useState } from 'react';
import { showTimedToast } from '../utils/toast';
import {
    createSpecFileListValue,
    isFirmwareSpecTitle,
    isImageSpecTitle,
    isUploadSpecTitle,
    isSpecFileUploadField,
    isWiringDiagramSpecTitle,
    inferSpecFileKind,
    modelSpecsNeedAssetLoad,
    parseSpecFileListValue,
    WIRING_DIAGRAM_MAX_UPLOAD_BYTES,
} from '../utils/specFiles';
import {
    alignSpecsWithRegistrationConfig,
    getMinNormalSpecRowsForConfig,
    getVisibleSpecs,
    isDefaultRegistrationSpec,
    isTrailingFullWidthSpec,
    normalizeProductRegistrationConfig,
    orderSpecsCustomBeforeTrailing,
} from '../utils/productRegistrationConfig';
import { SIDO_OPTIONS, getGugunOptions } from '../constants/koreaRegions';
import SpecFieldInput from './SpecFieldInput';
import AppleSwitchCheckbox from './AppleSwitchCheckbox';
import { getApiBaseUrl } from '../apiBase';

const ITEMS_PER_PAGE = 25;
const JSON_HEADERS = {
    'Content-Type': 'application/json'
};

const formatRejectHistoryInlineText = (model) => {
    const list = Array.isArray(model?.reviewRejectHistory) ? model.reviewRejectHistory : [];
    if (list.length === 0) {
        return '';
    }
    return list
        .map((entry, idx) => `승인 ${idx + 1}차 거부 내용:${String(entry?.reason || '').trim() || '-'}`)
        .join(', ');
};

const getSpecUploadAccept = (title) => {
    if (isImageSpecTitle(title)) {
        return 'image/*';
    }
    if (isFirmwareSpecTitle(title)) {
        return '.hex,.bin,.zip,.rar,.7z,.txt,.pdf,.xlsx,.xls,.xlsb';
    }
    if (isWiringDiagramSpecTitle(title)) {
        return '.pdf,.xlsx,.xls,.xlsb';
    }
    return undefined;
};

const SpecList = ({
    models = [],
    onUpdateModel,
    onDeleteModel,
    onApproveModel,
    onRejectModel,
    canEditProduct = false,
    canViewDetail = true,
    canManagePermissions = false,
    canDownloadFirmware = true,
    canDownloadWiringDiagram = false,
    showTypeFilter = false,
    typeFilters = { adjuster: true, controller: true },
    onToggleTypeFilter,
    editRequest = null,
    onEditRequestHandled,
    currentUserName = '',
    allowPendingExpand = false,
    showReviewMeta = false,
    headerExtra = null,
    renderCollapsedExtra,
    specDropdownOptions = [],
    productRegistrationConfig,
    allowAddDropdownOption = false,
    onAddDropdownOption,
    expandFromMapRequest = null,
    onExpandFromMapHandled,
    enableRowSelect = false,
    selectedRowIds = [],
    onToggleRowSelect,
    isRowSelectable,
    /** true면 접힌 목록 행에 모델명만 표시(현장명·세대수 숨김) */
    listSummaryModelNameOnly = false,
    /** 메인 리스트 vs 모델별 사양 리스트 UI 구분 */
    listVariant = 'mainList',
    /** 모델별 전용: 카드 덱 형태 목록 */
    presentationVariant = 'default',
    /** 섹션 상단 제목(h3) 숨김 — 외부 패널에서 제목을 쓸 때 */
    omitSectionHeading = false,
    /** 승인 목록 등: 상세 열 때만 이미지·파일 payload 로드 */
    lazyLoadAssets = true,
    assetListScope = '',
    onLoadModelAssets,
    onModelExpanded,
    loadingAssetModelIds = {},
}) => {
    const assetLoadingEnabled = lazyLoadAssets || typeof onLoadModelAssets === 'function';
    const getAssetLoadKey = useCallback((modelId) => {
        const id = Number(modelId);
        if (!assetListScope || Number.isNaN(id)) {
            return String(modelId);
        }
        return `${assetListScope}:${id}`;
    }, [assetListScope]);
    const [expandedModelId, setExpandedModelId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [editingModelId, setEditingModelId] = useState(null);
    const [editingDraft, setEditingDraft] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVisible, setToastVisible] = useState(false);
    const [toastType, setToastType] = useState('error');
    const [lightboxImage, setLightboxImage] = useState(null);
    const [pendingScrollModelId, setPendingScrollModelId] = useState(null);
    const [localAssetPendingKeys, setLocalAssetPendingKeys] = useState({});
    const onLoadModelAssetsRef = useRef(onLoadModelAssets);
    const onModelExpandedRef = useRef(onModelExpanded);
    const lastExpandedAssetLoadIdRef = useRef(null);
    const getReviewStatusNorm = (model) => String(model?.reviewStatus ?? 'approved').trim().toLowerCase();
    const isApprovalRejectedList = (model) => getReviewStatusNorm(model) === 'rejected';
    const hasReviewRejectHistory = (model) => Array.isArray(model?.reviewRejectHistory) && model.reviewRejectHistory.length > 0;
    /** 승인 거부 상태(rejected)일 때만 작성자 제한 */
    const isPostRejectAuthorOnlyList = (model) => getReviewStatusNorm(model) === 'rejected';
    /** 신규등록 거부(rejected) 또는 수정·삭제 요청 거부 후 이력만 남은 경우(approved + history) */
    const showRejectDetailUnderMemo = (model) => (
        isApprovalRejectedList(model)
        || (getReviewStatusNorm(model) === 'approved' && hasReviewRejectHistory(model))
    );
    const showGeneralListReviewBadge = (model) => {
        if (allowPendingExpand) {
            return false;
        }
        const rs = getReviewStatusNorm(model);
        return rs === 'pending' || rs === 'rejected';
    };
    const isOwnerModel = (model) => (
        String(model?.pendingRequesterName || '').trim()
        && String(model?.pendingRequesterName || '').trim() === String(currentUserName || '').trim()
    );
    const hasPermissionManagerBypass = canManagePermissions;
    /** 승인 검토중: 리스트 수정 권한·권한관리자만 열람/수정, 거부: 작성자·권한관리자만 */
    const isPendingBlockedForUser = (model) => (
        !allowPendingExpand
        && !hasPermissionManagerBypass
        && !canEditProduct
        && getReviewStatusNorm(model) === 'pending'
    );
    const cannotEditModelForUser = (model) => {
        if (hasPermissionManagerBypass || allowPendingExpand) {
            return false;
        }
        if (getReviewStatusNorm(model) === 'pending') {
            return !canEditProduct;
        }
        if (isPostRejectAuthorOnlyList(model)) {
            return !isOwnerModel(model);
        }
        return false;
    };
    /** 일반 목록에서 세부 열람 제한(잠금 표시 등) */
    const isMainListDetailLockedForUser = (model) => {
        if (allowPendingExpand || hasPermissionManagerBypass) {
            return false;
        }
        if (isPendingBlockedForUser(model)) {
            return true;
        }
        return isPostRejectAuthorOnlyList(model) && !isOwnerModel(model);
    };

    const totalPages = Math.max(1, Math.ceil(models.length / ITEMS_PER_PAGE));
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const pagedModels = models.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const showToast = useCallback((message, type = 'error') => {
        setToastType(type);
        showTimedToast({
            setMessage: setToastMessage,
            setVisible: setToastVisible,
            message,
        });
    }, []);

    useEffect(() => {
        onLoadModelAssetsRef.current = onLoadModelAssets;
    }, [onLoadModelAssets]);

    useEffect(() => {
        onModelExpandedRef.current = onModelExpanded;
    }, [onModelExpanded]);

    const triggerModelAssetLoad = useCallback((modelId) => {
        if (!modelId) {
            return Promise.resolve(null);
        }
        const loader = typeof onLoadModelAssetsRef.current === 'function'
            ? onLoadModelAssetsRef.current
            : (typeof onModelExpandedRef.current === 'function' ? onModelExpandedRef.current : null);
        if (!loader) {
            return Promise.resolve(null);
        }
        const pendingKey = getAssetLoadKey(modelId);
        setLocalAssetPendingKeys((prev) => ({ ...prev, [pendingKey]: true }));
        return Promise.resolve(loader(modelId))
            .catch(() => null)
            .finally(() => {
                setLocalAssetPendingKeys((prev) => {
                    const next = { ...prev };
                    delete next[pendingKey];
                    return next;
                });
            });
    }, [getAssetLoadKey]);

    useEffect(() => {
        if (expandedModelId == null || !assetLoadingEnabled) {
            lastExpandedAssetLoadIdRef.current = null;
            return;
        }
        if (lastExpandedAssetLoadIdRef.current === expandedModelId) {
            return;
        }
        lastExpandedAssetLoadIdRef.current = expandedModelId;
        triggerModelAssetLoad(expandedModelId);
    }, [expandedModelId, assetLoadingEnabled, triggerModelAssetLoad]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
            setExpandedModelId(null);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (!editRequest || !editRequest.key || !canEditProduct || editingModelId) {
            return;
        }
        const matchById = editRequest.id
            ? models.find((model) => Number(model.id) === Number(editRequest.id))
            : null;
        const match = matchById || models.find((model) => (
            String(model.siteName || '').trim() === String(editRequest.siteName || '').trim()
            && String(model.name || '').trim() === String(editRequest.productName || '').trim()
            && (!editRequest.type || String(model.type || '').trim() === String(editRequest.type || '').trim())
        ));
        if (match) {
            const matchIndex = models.findIndex((model) => model.id === match.id);
            if (matchIndex >= 0) {
                const targetPage = Math.floor(matchIndex / ITEMS_PER_PAGE) + 1;
                if (targetPage !== currentPage) {
                    setCurrentPage(targetPage);
                }
            }
            startEdit(match);
            setPendingScrollModelId(match.id);
            if (typeof onEditRequestHandled === 'function') {
                onEditRequestHandled(editRequest.key);
            }
        }
    }, [editRequest, models, canEditProduct, editingModelId, currentPage]);

    useEffect(() => {
        if (!pendingScrollModelId) {
            return;
        }
        if (!pagedModels.some((model) => model.id === pendingScrollModelId)) {
            return;
        }
        const raf = window.requestAnimationFrame(() => {
            const targetEl = document.getElementById(`model-row-${pendingScrollModelId}`);
            if (targetEl) {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            setPendingScrollModelId(null);
        });
        return () => window.cancelAnimationFrame(raf);
    }, [pendingScrollModelId, pagedModels]);

    const writeClientLog = async ({ actionType, targetType, targetId, description, metadata }) => {
        let userName = currentUserName;

        if (!userName) {
            try {
                const raw = localStorage.getItem('bns-user');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    userName = parsed?.name || '';
                }
            } catch (e) {
                // ignore
            }
        }

        if (!userName) {
            return;
        }

        try {
            const res = await fetch(`${getApiBaseUrl()}/logs/event`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    requesterName: userName,
                    actionType,
                    targetType,
                    targetId,
                    description,
                    metadata,
                })
            });
            if (!res.ok) {
                console.error('[SpecList] 로그 저장 실패:', res.status, actionType);
            }
        } catch (error) {
            console.error('[SpecList] 로그 fetch 오류:', error);
        }
    };

    useEffect(() => {
        if (!expandFromMapRequest?.key || expandFromMapRequest.modelId == null) {
            return;
        }
        const modelId = expandFromMapRequest.modelId;
        const idx = models.findIndex((m) => Number(m.id) === Number(modelId));
        const finish = () => {
            if (typeof onExpandFromMapHandled === 'function') {
                onExpandFromMapHandled();
            }
        };
        if (idx < 0) {
            finish();
            return;
        }
        if (editingModelId && editingModelId !== modelId) {
            finish();
            return;
        }
        if (editingModelId && editingModelId === modelId) {
            setExpandedModelId(modelId);
            setPendingScrollModelId(modelId);
            finish();
            return;
        }
        const selectedModel = models.find((item) => item.id === modelId);
        if (isPendingBlockedForUser(selectedModel)) {
            showToast('승인 검토중에는 세부사항을 열람할 수 없습니다.', 'error');
            finish();
            return;
        }
        if (!allowPendingExpand && !hasPermissionManagerBypass && isPostRejectAuthorOnlyList(selectedModel) && !isOwnerModel(selectedModel)) {
            showToast('승인 거부된 리스트는 작성자만 세부사항을 열람할 수 있습니다.', 'error');
            finish();
            return;
        }
        const pendingEditable = getReviewStatusNorm(selectedModel) === 'pending' && canEditProduct;
        if (!allowPendingExpand && !isPostRejectAuthorOnlyList(selectedModel) && !canViewDetail && !pendingEditable) {
            finish();
            return;
        }
        const targetPage = Math.floor(idx / ITEMS_PER_PAGE) + 1;
        setCurrentPage(targetPage);
        setExpandedModelId(modelId);
        writeClientLog({
            actionType: 'list_detail_view',
            targetType: 'product',
            targetId: modelId,
            description: '세부사항 확인',
            metadata: {
                siteName: selectedModel?.siteName || '',
                productName: selectedModel?.name || '',
            },
        });
        setPendingScrollModelId(modelId);
        finish();
    }, [expandFromMapRequest, models, editingModelId, canViewDetail, canEditProduct, allowPendingExpand, onExpandFromMapHandled, currentUserName, showToast, hasPermissionManagerBypass]);

    useEffect(() => {
        if (expandedModelId == null) {
            return;
        }
        const open = models.find((item) => item.id === expandedModelId);
        if (!open || editingModelId === expandedModelId) {
            return;
        }
        if (isPendingBlockedForUser(open)) {
            setExpandedModelId(null);
            return;
        }
        if (!allowPendingExpand && !hasPermissionManagerBypass && isPostRejectAuthorOnlyList(open) && !isOwnerModel(open)) {
            setExpandedModelId(null);
        }
    }, [models, expandedModelId, editingModelId, allowPendingExpand, canEditProduct, currentUserName, hasPermissionManagerBypass]);

    const toggleExpand = (modelId) => {
        if (editingModelId && editingModelId !== modelId) {
            showToast('리스트 수정 중에는 다른 리스트를 선택할 수 없습니다.', 'error');
            return;
        }
        if (editingModelId && editingModelId === modelId) {
            return;
        }

        const selectedModel = models.find((item) => item.id === modelId);
        if (isPendingBlockedForUser(selectedModel)) {
            showToast('승인 검토중에는 세부사항을 열람할 수 없습니다.', 'error');
            return;
        }
        if (!allowPendingExpand && !hasPermissionManagerBypass && isPostRejectAuthorOnlyList(selectedModel) && !isOwnerModel(selectedModel)) {
            showToast('승인 거부된 리스트는 작성자만 세부사항을 열람할 수 있습니다.', 'error');
            return;
        }
        const pendingEditable = getReviewStatusNorm(selectedModel) === 'pending' && canEditProduct;
        if (!allowPendingExpand && !isPostRejectAuthorOnlyList(selectedModel) && !canViewDetail && !pendingEditable) {
            showToast('세부사항 열람 권한이 없습니다 관리자에게 문의 바랍니다.', 'error');
            return;
        }

        const nextExpanded = expandedModelId === modelId ? null : modelId;
        setExpandedModelId(nextExpanded);

        if (nextExpanded) {
            const model = models.find((item) => item.id === modelId);
            writeClientLog({
                actionType: 'list_detail_view',
                targetType: 'product',
                targetId: modelId,
                description: '세부사항 확인',
                metadata: {
                    siteName: model?.siteName || '',
                    productName: model?.name || '',
                },
            });
        }
    };

    const startEdit = async (model) => {
        if (!canEditProduct) {
            showToast('리스트 수정 권한이 없습니다.', 'error');
            return;
        }
        if (cannotEditModelForUser(model)) {
            showToast(
                getReviewStatusNorm(model) === 'pending'
                    ? '승인 검토중인 항목은 리스트 수정 권한이 있는 사용자만 수정할 수 있습니다.'
                    : '승인 거부된 리스트는 작성자만 수정할 수 있습니다.',
                'error',
            );
            return;
        }

        let sourceModel = model;
        if (assetLoadingEnabled && model?.specsAssetsDeferred) {
            const loaded = await triggerModelAssetLoad(model.id);
            if (loaded) {
                sourceModel = loaded;
            }
        }

        setEditingModelId(sourceModel.id);
        setExpandedModelId(sourceModel.id);
        const nextSpecs = alignSpecsWithRegistrationConfig(sourceModel.type, sourceModel.specs || [], productRegistrationConfig);
        setEditingDraft({
            id: sourceModel.id,
            siteName: sourceModel.siteName,
            name: sourceModel.name,
            householdCount: sourceModel.householdCount || '',
            type: sourceModel.type,
            sido: sourceModel.sido || '',
            gugun: sourceModel.gugun || '',
            dong: sourceModel.dong || '',
            detailAddress: sourceModel.detailAddress || '',
            specs: nextSpecs,
        });
    };

    const cancelEdit = () => {
        setEditingModelId(null);
        setEditingDraft(null);
        setIsSaving(false);
        setIsDeleting(false);
    };

    const saveEdit = async () => {
        if (!editingDraft || !onUpdateModel) {
            return;
        }

        const hasEmptySpecTitle = editingDraft.specs.some(
            (spec) => !spec.fullWidth && !String(spec.title || '').trim(),
        );
        if (hasEmptySpecTitle) {
            showToast('사양 제목을 입력해주세요.', 'error');
            return;
        }

        setIsSaving(true);
        const result = await onUpdateModel({
            ...editingDraft,
            specs: orderSpecsCustomBeforeTrailing(editingDraft.specs),
        });
        setIsSaving(false);

        const ok = result === true || result?.ok === true;
        if (ok) {
            cancelEdit();
            setExpandedModelId(null);
            showToast('수정이 완료되었습니다.', 'success');
        } else {
            const message = typeof result === 'string'
                ? result
                : String(result?.message || '').trim();
            showToast(message || '수정 저장에 실패했습니다.', 'error');
        }
    };

    const deleteModel = async (modelId) => {
        if (!onDeleteModel) {
            return;
        }

        const okToDelete = window.confirm('삭제 요청을 올리시겠습니까? 승인 완료 후 실제 삭제됩니다.');
        if (!okToDelete) {
            return;
        }

        setIsDeleting(true);
        const ok = await onDeleteModel(modelId);
        setIsDeleting(false);

        if (ok) {
            cancelEdit();
            setExpandedModelId(null);
            showToast('삭제 요청이 등록되었습니다. 승인 후 삭제됩니다.', 'success');
        } else {
            showToast('삭제 요청에 실패했습니다.', 'error');
        }
    };

    const updateDraftField = (field, value) => {
        setEditingDraft((prev) => ({ ...prev, [field]: value }));
    };

    const updateDraftSpec = (specIndex, value) => {
        setEditingDraft((prev) => {
            const nextSpecs = [...prev.specs];
            nextSpecs[specIndex] = { ...nextSpecs[specIndex], details: value };
            return { ...prev, specs: nextSpecs };
        });
    };

    const updateDraftSpecTitle = (specIndex, value) => {
        setEditingDraft((prev) => {
            if (!prev) {
                return prev;
            }
            const nextSpecs = [...prev.specs];
            nextSpecs[specIndex] = { ...nextSpecs[specIndex], title: value };
            return { ...prev, specs: nextSpecs };
        });
    };

    const updateDraftSpecFile = (specIndex, kind) => (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) {
            return;
        }
        if (kind === 'wiringPdf') {
            const oversized = files.find((file) => Number(file?.size || 0) > WIRING_DIAGRAM_MAX_UPLOAD_BYTES);
            if (oversized) {
                showToast('결선도/발주서/체크리스트 파일은 20MB 이하만 업로드할 수 있습니다.', 'error');
                e.target.value = '';
                return;
            }
        }

        const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('파일 읽기 실패'));
            reader.readAsDataURL(file);
        });

        Promise.all(files.map(async (file) => ({
            kind,
            name: file.name,
            mimeType: file.type,
            dataUrl: await readFileAsDataUrl(file),
            description: '',
        })))
            .then((nextFiles) => {
                const existing = parseSpecFileListValue(editingDraft?.specs?.[specIndex]?.details);
                updateDraftSpec(specIndex, createSpecFileListValue({ files: [...existing, ...nextFiles] }));
            })
            .catch(() => {
                showToast('파일을 읽는 중 오류가 발생했습니다.', 'error');
            });
        e.target.value = '';
    };

    const clearDraftSpecFile = (specIndex, fileIndex) => {
        const existing = parseSpecFileListValue(editingDraft?.specs?.[specIndex]?.details);
        const next = existing.filter((_, idx) => idx !== fileIndex);
        updateDraftSpec(specIndex, next.length > 0 ? createSpecFileListValue({ files: next }) : '');
    };

    const updateDraftUploadFileDescription = (specIndex, fileIndex, description) => {
        const existing = parseSpecFileListValue(editingDraft?.specs?.[specIndex]?.details);
        const target = existing[fileIndex];
        if (!target || (target.kind !== 'firmware' && target.kind !== 'wiringPdf')) {
            return;
        }
        const next = existing.map((file, idx) => (idx === fileIndex ? { ...file, description } : file));
        updateDraftSpec(specIndex, createSpecFileListValue({ files: next }));
    };

    const addDraftSpec = () => {
        setEditingDraft((prev) => {
            if (!prev) {
                return prev;
            }

            const specCount = prev.specs.filter((spec) => !isTrailingFullWidthSpec(spec)).length;
            const normalized = normalizeProductRegistrationConfig(productRegistrationConfig);
            const typeKey = prev.type === 'controller' ? 'controller' : 'adjuster';
            const configField = normalized[typeKey].fields[specCount];
            const newSpec = {
                title: '',
                inputType: configField?.inputType || 'text',
                details: '',
                customTitle: true,
                fullWidth: false,
            };

            return {
                ...prev,
                specs: orderSpecsCustomBeforeTrailing([...prev.specs, newSpec]),
            };
        });
    };

    const removeDraftSpec = () => {
        setEditingDraft((prev) => {
            if (!prev) {
                return prev;
            }

            const nonFullSpecs = prev.specs.filter((spec) => !isTrailingFullWidthSpec(spec));
            const minRows = getMinNormalSpecRowsForConfig(prev.type, productRegistrationConfig);
            if (nonFullSpecs.length <= minRows) {
                showToast(`기본 사양 ${minRows}개는 유지되어야 합니다.`, 'error');
                return prev;
            }

            const nextNonFull = nonFullSpecs.slice(0, -1);
            const trailing = prev.specs.filter((spec) => isTrailingFullWidthSpec(spec));
            return {
                ...prev,
                specs: orderSpecsCustomBeforeTrailing([...nextNonFull, ...trailing]),
            };
        });
    };

    const toggleDraftSpecHidden = (specIndex) => {
        setEditingDraft((prev) => {
            if (!prev) {
                return prev;
            }
            const spec = prev.specs[specIndex];
            if (!spec || !isDefaultRegistrationSpec(spec)) {
                return prev;
            }
            const nextSpecs = [...prev.specs];
            nextSpecs[specIndex] = { ...spec, hidden: !spec.hidden };
            return { ...prev, specs: nextSpecs };
        });
    };

    const movePage = (page) => {
        if (page < 1 || page > totalPages) {
            return;
        }

        setCurrentPage(page);
        setExpandedModelId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        writeClientLog({
            actionType: 'list_page_move',
            targetType: 'list_page',
            targetId: page,
            description: `리스트 페이지 이동(${page})`,
        });
    };

    const getTypeLabel = (type) => {
        return type === 'adjuster' ? '조절기' : '제어기';
    };

    const getReviewDiffSections = (model) => {
        const initial = Array.isArray(model?.reviewDiffInitial)
            ? model.reviewDiffInitial
            : (Array.isArray(model?.reviewDiff) ? model.reviewDiff : []);
        const resubmit = Array.isArray(model?.reviewDiffResubmit) ? model.reviewDiffResubmit : [];
        return { initial, resubmit, all: [...initial, ...resubmit] };
    };

    const getReviewChangeMap = (model) => {
        const map = new Map();
        if (!showReviewMeta || model?.reviewEventType === 'delete') {
            return map;
        }
        const { all } = getReviewDiffSections(model);
        all.forEach((change) => {
            map.set(change.field, change);
        });
        return map;
    };

    const summarizeReviewSpecValue = (value, change) => {
        const field = String(change?.field || '');
        if (!field.startsWith('spec:')) {
            return String(value || '-');
        }
        const title = field.slice(5);
        if (!isUploadSpecTitle(title)) {
            return String(value || '-');
        }
        const files = parseSpecFileListValue(value, field.slice(5));
        if (files.length === 0) {
            return '-';
        }
        return files.map((file) => file.name || '(파일)').join(', ');
    };

    const formatReviewChangeText = (change) => (
        `변경전 : ${summarizeReviewSpecValue(change?.before, change)} / 변경후 : ${summarizeReviewSpecValue(change?.after, change)}`
    );

    const renderReviewDiffImagePreviews = (change) => {
        const field = String(change?.field || '');
        if (!field.startsWith('spec:')) {
            return null;
        }
        const specTitle = field.slice('spec:'.length);
        if (!isImageSpecTitle(specTitle)) {
            return null;
        }
        const renderSide = (label, value) => {
            const files = parseSpecFileListValue(value, specTitle).filter(
                (file) => inferSpecFileKind(file, specTitle) === 'image' && String(file?.dataUrl || '').trim(),
            );
            if (files.length === 0) {
                return null;
            }
            return (
                <div className="review-diff-image-side" key={label}>
                    <span className="review-diff-image-side-label">{label}</span>
                    <div className="spec-image-grid">
                        {files.map((file, idx) => (
                            <div key={`${label}-${file.name}-${idx}`} className="spec-image-item">
                                <img
                                    src={file.dataUrl}
                                    alt={file.name || specTitle}
                                    className="spec-upload-preview"
                                    onClick={() => setLightboxImage({ src: file.dataUrl, alt: file.name || specTitle })}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            );
        };
        const before = renderSide('변경전', change?.before);
        const after = renderSide('변경후', change?.after);
        if (!before && !after) {
            return null;
        }
        return (
            <div className="review-diff-image-previews">
                {before}
                {after}
            </div>
        );
    };

    const renderReviewDiffList = (changes, keyPrefix) => {
        if (!Array.isArray(changes) || changes.length === 0) {
            return null;
        }
        return (
            <ul className="review-diff-list">
                {changes.map((change, index) => (
                    <li key={`${keyPrefix}-${change.field}-${index}`}>
                        <span className="review-diff-label">{change.label}:</span> {formatReviewChangeText(change)}
                        {renderReviewDiffImagePreviews(change)}
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div
            className={`model-list-section model-list-section--${listVariant}${presentationVariant === 'modelSpecDeck' ? ' spec-list-presentation--modelSpecDeck' : ''}`}
            role="region"
            aria-label={listVariant === 'modelSpecList' ? '모델별 사양 리스트' : '전체 사양 관리 목록'}
        >
            <div className="model-list-head">
                {!omitSectionHeading && (
                    <h3>{listVariant === 'modelSpecList' ? '모델별 사양 리스트' : '사양 관리'}</h3>
                )}
                <div className={`model-list-head-right${omitSectionHeading ? ' model-list-head-right--full' : ''}`}>
                    {headerExtra}
                    {showTypeFilter && (
                        <div className="type-filter-buttons">
                            <button
                                type="button"
                                className={`button type-filter-button adjuster ${typeFilters.adjuster ? '' : 'off'}`}
                                onClick={() => onToggleTypeFilter && onToggleTypeFilter('adjuster')}
                            >
                                조절기
                            </button>
                            <button
                                type="button"
                                className={`button type-filter-button controller ${typeFilters.controller ? '' : 'off'}`}
                                onClick={() => onToggleTypeFilter && onToggleTypeFilter('controller')}
                            >
                                제어기
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {models.length === 0 && (
                <div className="empty-list-message">
                    {listVariant === 'modelSpecList' ? '모델별로 표시할 제품이 없습니다.' : '표시할 제품이 없습니다.'}
                </div>
            )}
            <div className={presentationVariant === 'modelSpecDeck' ? 'model-spec-deck-grid' : 'model-spec-deck-grid--passThrough'}>
            {pagedModels.map((model) => (
                <div
                    id={`model-row-${model.id}`}
                    key={model.id || `${model.siteName}-${model.name}`}
                    className={presentationVariant === 'modelSpecDeck' ? 'model-spec-deck-item' : undefined}
                >
                    <div
                        className={`model-name${!allowPendingExpand ? ' model-name-general' : ''}${listSummaryModelNameOnly && !allowPendingExpand ? ' model-name-summary-model-only' : ''}${presentationVariant === 'modelSpecDeck' ? ' model-spec-deck-row' : ''}${enableRowSelect ? ' model-name-with-selector' : ''}${isMainListDetailLockedForUser(model) ? ' model-name-detail-locked' : ''}`}
                        onClick={() => toggleExpand(model.id)}
                    >
                        {enableRowSelect && (
                            <span className="model-row-selector-wrap">
                                <AppleSwitchCheckbox
                                    checked={selectedRowIds.includes(model.id)}
                                    disabled={typeof isRowSelectable === 'function' ? !isRowSelectable(model) : false}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => onToggleRowSelect && onToggleRowSelect(model, e.target.checked)}
                                    aria-label={listSummaryModelNameOnly
                                        ? `행 선택: ${String(model.name || '').trim()}`
                                        : `행 선택: ${String(model.siteName || '').trim()} / ${String(model.name || '').trim()}`}
                                />
                            </span>
                        )}
                        <span className={`model-type-badge ${getTypeLabel(model.type)}`}>{getTypeLabel(model.type)}</span>
                        {allowPendingExpand && model.reviewStatus === 'pending' && (
                            <>
                                <span className="review-dot" />
                                <span className="review-event-chip">
                                    {model.reviewEventType === 'create'
                                        ? '신규 등록'
                                        : model.reviewEventType === 'delete'
                                            ? '삭제 대기중'
                                            : '기존 수정'}
                                </span>
                            </>
                        )}
                        <div className="model-list-main-cols">
                            {!listSummaryModelNameOnly && (
                                <div
                                    className="model-list-site"
                                    title={editingModelId === model.id
                                        ? `현장명: ${String(editingDraft?.siteName ?? '').trim()}`
                                        : `현장명: ${String(model.siteName ?? '').trim()}`}
                                >
                                    현장명: {editingModelId === model.id ? editingDraft?.siteName : model.siteName}
                                </div>
                            )}
                            <div
                                className={`model-list-product${presentationVariant === 'modelSpecDeck' ? ' model-spec-deck-product' : ''}`}
                                title={editingModelId === model.id
                                    ? `모델명: ${String(editingDraft?.name ?? '').trim()}`
                                    : `모델명: ${String(model.name ?? '').trim()}`}
                            >
                                {listSummaryModelNameOnly && presentationVariant === 'modelSpecDeck' ? (
                                    <span className="model-spec-deck-name-only">{editingModelId === model.id ? editingDraft?.name : model.name}</span>
                                ) : (
                                    <>모델명: {editingModelId === model.id ? editingDraft?.name : model.name}</>
                                )}
                            </div>
                            {!listSummaryModelNameOnly && (
                                <div
                                    className="model-list-household"
                                    title={editingModelId === model.id
                                        ? `세대수: ${String(editingDraft?.householdCount ?? '').trim()}`
                                        : `세대수: ${String(model.householdCount ?? '').trim()}`}
                                >
                                    세대수: {editingModelId === model.id ? editingDraft?.householdCount : (model.householdCount || '-')}
                                </div>
                            )}
                        </div>
                        <div className="model-name-trailing">
                            {showGeneralListReviewBadge(model) && (
                                <span className="model-list-review-plain">
                                    <span className="model-list-review-author">
                                        작성자:{String(model.pendingRequesterName || '').trim() || '-'}
                                    </span>
                                    {getReviewStatusNorm(model) === 'rejected' ? (
                                        <span className="model-list-review-status is-reject">승인 거부됨</span>
                                    ) : getReviewStatusNorm(model) === 'pending' ? (
                                        model.reviewEventType === 'delete' ? (
                                            <span className="model-list-review-status is-pending">삭제 승인 대기중</span>
                                        ) : model.reviewEventType === 'update' ? (
                                            <span className="model-list-review-status is-pending">재승인 검토중</span>
                                        ) : (
                                            <span className="model-list-review-status is-pending">승인 검토중</span>
                                        )
                                    ) : (
                                        <span className="model-list-review-status is-reject">승인 거부됨</span>
                                    )}
                                </span>
                            )}
                            {(canEditProduct || allowPendingExpand) && (
                                <div className="model-actions" onClick={(e) => e.stopPropagation()}>
                                    {canEditProduct && editingModelId === model.id ? (
                                        <>
                                            <button className="button button-add-spec model-edit-button" onClick={addDraftSpec} disabled={isSaving}>사양 추가</button>
                                            <button className="button button-cancel model-edit-button" onClick={removeDraftSpec} disabled={isSaving || isDeleting}>사양 제거</button>
                                            <button className="button model-edit-button" onClick={saveEdit} disabled={isSaving || isDeleting}>{isSaving ? '저장중...' : '저장'}</button>
                                            <button className="button button-cancel model-edit-button" onClick={() => deleteModel(model.id)} disabled={isSaving || isDeleting}>{isDeleting ? '삭제중...' : '삭제'}</button>
                                            <button className="button button-cancel model-edit-button" onClick={cancelEdit} disabled={isSaving || isDeleting}>취소</button>
                                        </>
                                    ) : allowPendingExpand ? (
                                        <>
                                            <button className="button model-edit-button" onClick={() => toggleExpand(model.id)}>
                                                {expandedModelId === model.id ? '상세 닫기' : '상세 보기'}
                                            </button>
                                            {expandedModelId === model.id && typeof onRejectModel === 'function' && (
                                                <button className="button button-cancel model-edit-button" onClick={() => onRejectModel(model.id)}>승인 거부</button>
                                            )}
                                            {expandedModelId === model.id && typeof onApproveModel === 'function' && (
                                                <button className="button model-edit-button" onClick={() => onApproveModel(model.id)}>최종 승인</button>
                                            )}
                                        </>
                                    ) : (
                                        <button
                                            className="button model-edit-button"
                                            onClick={() => startEdit(model)}
                                            disabled={cannotEditModelForUser(model)}
                                        >
                                            수정
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {typeof renderCollapsedExtra === 'function' && expandedModelId !== model.id && (
                        <div className="model-collapsed-extra">
                            {renderCollapsedExtra(model)}
                        </div>
                    )}
                    {expandedModelId === model.id && (
                        <div
                            className={presentationVariant === 'modelSpecDeck' ? 'model-spec-deck-expanded' : undefined}
                            style={presentationVariant === 'modelSpecDeck' ? undefined : { display: 'contents' }}
                        >
                            {assetLoadingEnabled && modelSpecsNeedAssetLoad(model) && (() => {
                                const assetLoadKey = getAssetLoadKey(model.id);
                                const isAssetLoading = !!loadingAssetModelIds[assetLoadKey]
                                    || !!localAssetPendingKeys[assetLoadKey];
                                if (!isAssetLoading) {
                                    return null;
                                }
                                return (
                                    <div className="spec-detail-asset-loading" role="status">
                                        파일 불러오는 중...
                                    </div>
                                );
                            })()}
                            {(() => {
                                const reviewChangeMap = getReviewChangeMap(model);
                                const siteChange = reviewChangeMap.get('siteName');
                                const nameChange = reviewChangeMap.get('productName');
                                if (!showReviewMeta || model.reviewEventType !== 'update' || (!siteChange && !nameChange)) {
                                    return null;
                                }
                                return (
                                    <div className="edit-top-row review-top-row">
                                        {siteChange && (
                                            <div className="spec-input">
                                                <label>
                                                    <span className="spec-changed-dot" />
                                                    현장명
                                                </label>
                                                <div className="review-inline-change">{formatReviewChangeText(siteChange)}</div>
                                            </div>
                                        )}
                                        {nameChange && (
                                            <div className="spec-input">
                                                <label>
                                                    <span className="spec-changed-dot" />
                                                    모델명
                                                </label>
                                                <div className="review-inline-change">{formatReviewChangeText(nameChange)}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            {editingModelId === model.id && (
                                <div className="top-required-row top-required-row-triple spec-edit-top-row">
                                    <div className="form-field top-required-item-40">
                                        <label className="field-label">현장명</label>
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={editingDraft?.siteName || ''}
                                            onChange={(e) => updateDraftField('siteName', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field top-required-item-40">
                                        <label className="field-label">모델명</label>
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={editingDraft?.name || ''}
                                            onChange={(e) => updateDraftField('name', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field top-required-item-20">
                                        <label className="field-label">세대수</label>
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={editingDraft?.householdCount || ''}
                                            onChange={(e) => updateDraftField('householdCount', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label className="field-label">시/도</label>
                                        <select
                                            className="field-input"
                                            value={editingDraft?.sido || ''}
                                            onChange={(e) => {
                                                updateDraftField('sido', e.target.value);
                                                updateDraftField('gugun', '');
                                            }}
                                        >
                                            <option value="">시/도</option>
                                            {SIDO_OPTIONS.map((item) => (
                                                <option key={item} value={item}>{item}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="field-label">구/군</label>
                                        <select
                                            className="field-input"
                                            value={editingDraft?.gugun || ''}
                                            onChange={(e) => {
                                                updateDraftField('gugun', e.target.value);
                                            }}
                                            disabled={!editingDraft?.sido}
                                        >
                                            <option value="">구/군</option>
                                            {getGugunOptions(editingDraft?.sido || '').map((item) => (
                                                <option key={item} value={item}>{item}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="field-label">상세주소</label>
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={editingDraft?.detailAddress || ''}
                                            onChange={(e) => updateDraftField('detailAddress', e.target.value)}
                                            placeholder="지번·도로명 등 상세주소"
                                        />
                                    </div>
                                </div>
                            )}
                            {showReviewMeta && model.reviewStatus === 'pending' && (() => {
                                const { initial, resubmit } = getReviewDiffSections(model);
                                const isCreate = model.reviewEventType === 'create';
                                const isDelete = model.reviewEventType === 'delete';
                                const initialTitle = isCreate ? '최초 등록 요청 내용' : '최초 수정 요청 내용';
                                return (
                                    <div className="review-diff-wrap">
                                        <h4>
                                            {isDelete
                                                ? '삭제 대기중 항목'
                                                : resubmit.length > 0
                                                    ? '승인 요청 · 재수정 내역'
                                                    : (isCreate ? '신규 등록 요청' : '수정 요청 내역')}
                                        </h4>
                                        {isDelete ? (
                                            <p>삭제 승인 요청된 리스트입니다. 최종 승인 시 실제로 삭제됩니다.</p>
                                        ) : (
                                            <>
                                                {resubmit.length > 0 ? (
                                                    <h5 className="review-diff-subtitle">{initialTitle}</h5>
                                                ) : null}
                                                {renderReviewDiffList(initial, 'initial') || (
                                                    isCreate
                                                        ? <p>신규 등록된 리스트입니다.</p>
                                                        : <p>최초 요청 변경 내역이 없습니다.</p>
                                                )}
                                                {resubmit.length > 0 ? (
                                                    <div className="review-diff-resubmit-block">
                                                        <h5 className="review-diff-subtitle">재수정 내용</h5>
                                                        {renderReviewDiffList(resubmit, 'resubmit')}
                                                    </div>
                                                ) : null}
                                            </>
                                        )}
                                        {!isDelete && initial.length === 0 && resubmit.length === 0 ? (
                                            <p className="review-diff-empty-note">표시할 변경 내역이 없습니다.</p>
                                        ) : null}
                                    </div>
                                );
                            })()}
                            {showReviewMeta && (
                                <div className="review-request-type-text">
                                    요청 유형: {model.reviewEventType === 'create' ? '리스트 등록' : model.reviewEventType === 'delete' ? '리스트 삭제' : '리스트 수정'}
                                </div>
                            )}
                            <div className={editingModelId === model.id ? 'form-fields-grid spec-edit-fields-grid' : 'spec-view-grid'}>
                            {(editingModelId === model.id
                                ? (editingDraft?.specs || [])
                                : getVisibleSpecs(model.specs || [])
                            ).map((spec, specIndex) => (
                                <React.Fragment key={specIndex}>
                                <div
                                    className={editingModelId === model.id
                                        ? `form-field ${spec.fullWidth ? 'full-width' : ''}${spec.hidden ? ' form-field--spec-hidden' : ''}`
                                        : `spec-view-item ${spec.fullWidth ? 'full-width' : ''}`}
                                >
                                    {editingModelId === model.id && spec.customTitle ? (
                                        <input
                                            className="field-input"
                                            type="text"
                                            value={spec.title || ''}
                                            onChange={(e) => updateDraftSpecTitle(specIndex, e.target.value)}
                                            placeholder="사양 제목 입력"
                                            disabled={!!spec.hidden}
                                        />
                                    ) : editingModelId === model.id && isDefaultRegistrationSpec(spec) ? (
                                        <div className="field-label-row">
                                            <label className="field-label">
                                                {showReviewMeta && model.reviewEventType !== 'delete' && getReviewDiffSections(model).all.some((change) => change.field === `spec:${spec.title}`) && (
                                                    <span className="spec-changed-dot" />
                                                )}
                                                {spec.title}
                                            </label>
                                            <button
                                                type="button"
                                                className="spec-hide-toggle"
                                                onClick={() => toggleDraftSpecHidden(specIndex)}
                                                aria-label={spec.hidden ? '기본 사양 다시 사용' : '기본 사양 숨김'}
                                                title={spec.hidden ? '다시 사용' : '숨김'}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <label className={editingModelId === model.id ? 'field-label' : ''}>
                                            {showReviewMeta && model.reviewEventType !== 'delete' && getReviewDiffSections(model).all.some((change) => change.field === `spec:${spec.title}`) && (
                                                <span className="spec-changed-dot" />
                                            )}
                                            {spec.title}
                                        </label>
                                    )}
                                    {(() => {
                                        const fileField = isSpecFileUploadField(spec)
                                            || parseSpecFileListValue(spec.details, spec.title).length > 0;
                                        return fileField;
                                    })() ? (
                                        <div className={`spec-file-view ${editingModelId === model.id ? '' : 'spec-view-file-box'}`}>
                                            {(() => {
                                                const fileInfos = parseSpecFileListValue(spec.details, spec.title);
                                                const resolvedFiles = fileInfos.map((file) => ({
                                                    ...file,
                                                    kind: inferSpecFileKind(file, spec.title),
                                                }));
                                                const imageFiles = resolvedFiles.filter(
                                                    (file) => file.kind === 'image' && String(file.dataUrl || '').trim(),
                                                );
                                                const pendingImageFiles = resolvedFiles.filter(
                                                    (file) => file.kind === 'image' && !String(file.dataUrl || '').trim(),
                                                );
                                                const assetLoadKey = getAssetLoadKey(model.id);
                                                const isAssetLoading = assetLoadingEnabled && (
                                                    !!loadingAssetModelIds[assetLoadKey]
                                                    || !!localAssetPendingKeys[assetLoadKey]
                                                );
                                                return (
                                                    <>
                                                        {isAssetLoading && (
                                                            <span className="spec-file-loading">파일 불러오는 중...</span>
                                                        )}
                                                        {!isAssetLoading && imageFiles.length > 0 && (
                                                            <div className="spec-image-grid">
                                                                {imageFiles.map((file, idx) => (
                                                                    <div key={`${file.name}-${idx}`} className="spec-image-item">
                                                                        <img
                                                                            src={file.dataUrl}
                                                                            alt={file.name || spec.title}
                                                                            className="spec-upload-preview"
                                                                            onClick={() => setLightboxImage({ src: file.dataUrl, alt: file.name || spec.title })}
                                                                        />
                                                                        {editingModelId === model.id && (
                                                                            <button
                                                                                type="button"
                                                                                className="spec-image-delete-button"
                                                                                onClick={() => clearDraftSpecFile(specIndex, idx)}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {!isAssetLoading && pendingImageFiles.length > 0 && imageFiles.length === 0 && (
                                                            <div className="spec-image-pending">
                                                                {pendingImageFiles.map((file, idx) => (
                                                                    <span key={`pending-${file.name}-${idx}`} className="spec-image-pending-name">
                                                                        {file.name || '이미지'}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {!isAssetLoading && isFirmwareSpecTitle(spec.title) && resolvedFiles.filter((file) => file.kind === 'firmware' && String(file.dataUrl || '').trim()).map((file, idx) => (
                                                            <div key={`${file.name}-${idx}`} className="spec-firmware-row-wrap">
                                                                <div className="spec-firmware-row">
                                                                    <span className="spec-firmware-name">{file.name}</span>
                                                                    {canDownloadFirmware && (
                                                                        <a
                                                                            className="button spec-download-button"
                                                                            href={file.dataUrl}
                                                                            download={file.name}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            다운로드
                                                                        </a>
                                                                    )}
                                                                </div>
                                                                {file.description && (
                                                                    <div className="spec-firmware-tooltip">
                                                                        {file.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {!isAssetLoading && isWiringDiagramSpecTitle(spec.title) && resolvedFiles.filter((file) => file.kind === 'wiringPdf' && String(file.dataUrl || '').trim()).map((file, idx) => (
                                                            <div key={`wiring-${file.name}-${idx}`} className="spec-firmware-row-wrap">
                                                                <div className="spec-firmware-row">
                                                                    <span className="spec-firmware-name">{file.name}</span>
                                                                    {canDownloadWiringDiagram && (
                                                                        <a
                                                                            className="button spec-download-button"
                                                                            href={file.dataUrl}
                                                                            download={file.name}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            다운로드
                                                                        </a>
                                                                    )}
                                                                </div>
                                                                {file.description && (
                                                                    <div className="spec-firmware-tooltip">
                                                                        {file.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {!isAssetLoading
                                                            && imageFiles.length === 0
                                                            && !resolvedFiles.some((file) => file.kind === 'firmware')
                                                            && !resolvedFiles.some((file) => file.kind === 'wiringPdf') && (
                                                            <span className="spec-file-empty">업로드된 파일 없음</span>
                                                        )}
                                                        {showReviewMeta && model.reviewEventType === 'update' && (() => {
                                                            const change = getReviewDiffSections(model).all.find((item) => item.field === `spec:${spec.title}`);
                                                            if (!change) {
                                                                return null;
                                                            }
                                                            return (
                                                                <div className="review-inline-change">
                                                                    {formatReviewChangeText(change)}
                                                                </div>
                                                            );
                                                        })()}
                                                    </>
                                                );
                                            })()}
                                            {editingModelId === model.id && (
                                                <div className="spec-file-actions">
                                                    <input
                                                        type="file"
                                                        multiple
                                                        disabled={!!spec.hidden}
                                                        accept={getSpecUploadAccept(spec.title)}
                                                        onChange={updateDraftSpecFile(
                                                            specIndex,
                                                            isImageSpecTitle(spec.title)
                                                                ? 'image'
                                                                : isFirmwareSpecTitle(spec.title)
                                                                    ? 'firmware'
                                                                    : 'wiringPdf'
                                                        )}
                                                    />
                                                </div>
                                            )}
                                            {editingModelId === model.id && (isFirmwareSpecTitle(spec.title) || isWiringDiagramSpecTitle(spec.title)) && (() => {
                                                const parsedFiles = parseSpecFileListValue((editingDraft?.specs || [])[specIndex]?.details);
                                                const rows = parsedFiles
                                                    .map((file, fileIndex) => ({ file, fileIndex }))
                                                    .filter(({ file }) => {
                                                        if (isFirmwareSpecTitle(spec.title)) {
                                                            return file.kind === 'firmware';
                                                        }
                                                        if (isWiringDiagramSpecTitle(spec.title)) {
                                                            return file.kind === 'wiringPdf';
                                                        }
                                                        return false;
                                                    });
                                                if (rows.length === 0) {
                                                    return null;
                                                }
                                                return rows.map(({ file, fileIndex }) => (
                                                    <div key={`${file.name}-${fileIndex}`} className="firmware-edit-row">
                                                        <div className="file-selected-row">
                                                            <span className="file-selected-name">{file.name}</span>
                                                            <button type="button" className="button button-cancel model-edit-button" onClick={() => clearDraftSpecFile(specIndex, fileIndex)}>삭제</button>
                                                        </div>
                                                        <textarea
                                                            className="field-input firmware-description-input"
                                                            value={file.description || ''}
                                                            onChange={(e) => updateDraftUploadFileDescription(specIndex, fileIndex, e.target.value)}
                                                            placeholder={
                                                                isWiringDiagramSpecTitle(spec.title)
                                                                    ? '결선도/발주서/체크리스트 메모를 입력해주세요.'
                                                                    : '펌웨어 파일 설명을 입력해주세요.'
                                                            }
                                                        />
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    ) : editingModelId === model.id ? (
                                        <>
                                            <SpecFieldInput
                                                spec={spec}
                                                productType={model.type}
                                                productRegistrationConfig={productRegistrationConfig}
                                                value={spec.details}
                                                onChange={(v) => updateDraftSpec(specIndex, v)}
                                                specDropdownOptions={specDropdownOptions}
                                                allowAddDropdownOption={allowAddDropdownOption}
                                                onAddDropdownOption={onAddDropdownOption}
                                                disabled={!!spec.hidden}
                                            />
                                            {showReviewMeta && model.reviewEventType !== 'delete' && (() => {
                                                const change = getReviewDiffSections(model).all.find((item) => item.field === `spec:${spec.title}`);
                                                if (!change) {
                                                    return null;
                                                }
                                                return (
                                                    <div className="review-inline-change">
                                                        {formatReviewChangeText(change)}
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    ) : (
                                        <div className="spec-view-value">{spec.details || '-'}</div>
                                    )}
                                </div>
                                {String(spec.title).trim() === '비고' && showRejectDetailUnderMemo(model) && (
                                    <div
                                        className={
                                            editingModelId === model.id
                                                ? 'form-field full-width spec-reject-under-memo'
                                                : 'spec-view-item full-width spec-reject-under-memo'
                                        }
                                    >
                                        <div className="spec-view-value spec-reject-history-inline">
                                            {formatRejectHistoryInlineText(model) || '등록된 승인 거부 내용이 없습니다.'}
                                        </div>
                                    </div>
                                )}
                                </React.Fragment>
                            ))}
                            {showRejectDetailUnderMemo(model)
                                && !(editingModelId === model.id ? editingDraft?.specs || [] : model.specs).some((s) => String(s.title || '').trim() === '비고') && (
                                <div
                                    className={
                                        editingModelId === model.id
                                            ? 'form-field full-width spec-reject-under-memo'
                                            : 'spec-view-item full-width spec-reject-under-memo'
                                    }
                                >
                                    <div className="spec-view-value spec-reject-history-inline">
                                        {formatRejectHistoryInlineText(model) || '등록된 승인 거부 내용이 없습니다.'}
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
                    )}
                </div>
            ))}
            </div>

            {totalPages > 1 && (
                <div className="pagination-wrap">
                    <button className="pagination-button" onClick={() => movePage(currentPage - 1)} disabled={currentPage === 1}>이전</button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                        <button
                            key={page}
                            className={`pagination-button ${page === currentPage ? 'active' : ''}`}
                            onClick={() => movePage(page)}
                        >
                            {page}
                        </button>
                    ))}
                    <button className="pagination-button" onClick={() => movePage(currentPage + 1)} disabled={currentPage === totalPages}>다음</button>
                </div>
            )}

            {toastMessage && (
                <div className={`toast-popup ${toastVisible ? 'show' : ''} ${toastType === 'success' ? 'toast-success' : 'toast-error'}`}>{toastMessage}</div>
            )}
            {lightboxImage && (
                <div className="lightbox-backdrop" onClick={() => setLightboxImage(null)}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
                        <img src={lightboxImage.src} alt={lightboxImage.alt} className="lightbox-image" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpecList;